<template>
    <div v-if="selectedHardware">
        <h3 class="text-lg font-semibold my-4">Equipment Order</h3>
        <LineItem>
            <template #default>
                {{ selectedHardware.label }}
            </template>
            <template #description>
                {{ selectedHardware.description }}
            </template>
            <template #price>
                {{ formatCurrency(selectedHardware.price) }}
                <span v-if="selectedHardware.max_quantity !== 1">/ea</span>
            </template>
        </LineItem>

        <div
            v-if="false && selectedHardware.max_quantity !== 1"
            class="flex gap-6 items-center my-4"
        >
            <div class="mb-1 mt-2 border-gray-200 h-2 border-t"></div>
            <label for="hardwareQty" class="font-medium text-zinc-900"> Qty </label>
            <select
                id="hardwareQty"
                name="hardwareQty"
                v-model="store.$state.selectedHardwareQty"
                class="rounded-md border-0 py-1.5 pl-3 pr-10 text-zinc-900 ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-primary-500 sm:text-sm sm:leading-6"
                :disabled="cart.$state.couponPromo ? true : false"
            >
                <option v-for="i in 20" :value="i" :key="i">
                    {{ i }}
                </option>
            </select>
            <div class="flex-auto text-right">x {{ store.$state.selectedHardwareQty }}</div>
        </div>

        <div class="mb-1 mt-2 border-gray-200 h-2 border-t"></div>

        <div class="my-4">
            <RadioInput
                v-model="cart.$state.shipping"
                label="Shipping method"
                name="shipping-method"
                :options="shippingOptionsFormatted"
            />
        </div>

        <div class="mb-1 mt-2 border-gray-200 h-2 border-t"></div>

        <!-- TOTALS -->
        <TotalSection />

        <div class="mb-1 mt-2 border-gray-200 h-2 border-t"></div>

        <StripeElements
            v-if="selectedHardware"
            :handler="paymentHandler"
            :validatorFn="prePaymentValidator"
        />

        <!-- PAYMENT MODAL -->
        <ProgressModal
            v-if="state.showProgressModal"
            :steps="steps"
            :loading="state.showProgressModal"
        >
            <template v-if="state.orderComplete">
                <p class="text-center my-6">
                    <template v-if="redirectCountDown >= 0">
                        You will be redirected in {{ redirectCountDown }} seconds
                    </template>
                    <template v-else> Redirecting you now... </template>
                </p>
                <a class="button button--primary" :href="state.orderCompletePage">
                    Click here to Get Started!
                </a>
            </template>
        </ProgressModal>
    </div>
</template>

<script setup lang="ts">
import { useCheckoutStore } from '@/store/checkout'
import { useCartStore } from '@/store/cart'
import TotalSection from '@/components/Shared/TotalSection.vue'
import { createHardwareOrder } from '@/api/memberships'
import { trackOrder } from '@/utils/trackers'
import { computed, reactive, ref } from 'vue'
import { formatCurrency } from '@/utils/currency'
import { shipping as shippingOptions } from '@/data'
import RadioInput from '@/components/Form/RadioInput.vue'
import { isValidEmail } from '@/api/memberships'
import ProgressModal from '../Shared/ProgressModal.vue'
import StripeElements from '../Shared/StripeElements.vue'
import LineItem from '../Shared/LineItem.vue'

const store = useCheckoutStore()
const cart = useCartStore()
const state = reactive({
    paymentComplete: false,
    equipmentComplete: false,
    trackingComplete: false,
    finalizingComplete: false,
    orderComplete: false,
    trackingResponse: {
        tracked: [] as any[],
        errors: [] as unknown[],
    },
    redirectInterval: null as number | null,
    showProgressModal: false,
    orderCompletePage: `/order-submitted/${document.location.search}`,
})

const selectedHardware = computed(() => {
    return cart.selectedHardware?.[0]
})

const shippingOptionsFormatted = (selectedHardware.value?.shipping_options || shippingOptions).map((option) => {
    return {
        ...option,
        price: option.price === 0 ? 'Free' : `${formatCurrency(option.price)}`,
    }
})

const redirectCountDown = ref(5)

const steps = computed((): ProgressStep[] => [
    {
        name: 'Verifying payment',
        status: state.paymentComplete ? 'complete' : 'current',
    },
    {
        name: 'Processing order',
        status: !state.paymentComplete
            ? 'upcoming'
            : state.equipmentComplete
                ? 'complete'
                : 'current',
    },
    {
        name: 'Order complete',
        status: !state.finalizingComplete
            ? 'upcoming'
            : state.orderComplete
                ? 'complete'
                : 'current',
    },
])

const redirect = () => {
    window.location.href = state.orderCompletePage
}

const finalizeOrder = () => new Promise((resolve) => {
    setTimeout(() => {
        state.trackingComplete = true
        setTimeout(() => {
            state.finalizingComplete = true
            setTimeout(() => {
                state.orderComplete = true
                state.redirectInterval = window.setInterval(() => {
                    if (redirectCountDown.value <= 1) {
                        if (state.redirectInterval) {
                            clearInterval(state.redirectInterval)
                        }
                        state.showProgressModal = false
                        redirect()
                    }
                    redirectCountDown.value--
                }, 1000)
                resolve(true)
            }, 350)
        }, 250)
    }, 150)
})

const handleTracking = (membershipOrder: any, equipmentOrder: any) => {
    try {
        state.trackingResponse = trackOrder(membershipOrder, equipmentOrder)
    } catch (error) {
        console.error(error)
    }
}

const resetProgress = () => {
    state.paymentComplete = false
    state.equipmentComplete = false
    state.trackingComplete = false
    state.finalizingComplete = false
    state.orderComplete = false
    state.trackingResponse = {
        tracked: [],
        errors: [],
    }
    state.redirectInterval = null
    state.showProgressModal = false
}

const handleEquipmentPurchase = async(paymentMethodId: string, stripeCustomerId: string) => {
    const params = store.getCheckoutParams()
    const orderParams = {
        ...params,
        payment_method: paymentMethodId || store.$state.paymentMethodId,
        customer_id: stripeCustomerId || store.$state.stripeCustomerId || undefined,
        account_id: store.$state.accountId || undefined,
    }
    const orderResponse = await createHardwareOrder(orderParams)
    state.equipmentComplete = true
    return orderResponse
}

const prePaymentValidator = async(): Promise<boolean> => {
    const isEmailValid = isValidEmail(store.$state.email)
    if (!isEmailValid) {
        return Promise.reject('Please enter a valid email address')
    }
    return Promise.resolve(true)
}

const paymentHandler = async(paymentMethodId: string, stripeCustomerId: string) => {
    state.showProgressModal = true
    setTimeout(() => {
        state.paymentComplete = true
    }, 500)
    try {
        const equipmentOrder = await handleEquipmentPurchase(paymentMethodId, stripeCustomerId)
        handleTracking(null, equipmentOrder)
        await finalizeOrder()
        store.$state.steps.paymentMethod.complete = true
        return Promise.resolve(true)
    } catch (e: any) {
        resetProgress()
        if (e?.message && typeof e.message === 'string') {
            try {
                const message = JSON.parse(e.message || '{}')
                if (message?.message) {
                    throw new Error(message.message)
                }
            } catch (error) {
                //throw e
            }
        } else if (e?.message && typeof e.message !== 'string') {
            console.error(e)
            throw new Error('Something went wrong. Please contact support')
        }
        throw e
    }
}
</script>
